import React from 'react';
import { Button } from '@/ui-components/ui/button';
import { Textarea } from '@/ui-components/ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/util-components/dropdown-menu';
import { ChevronDown, Loader2, RefreshCcw, Sparkles, Wand2 } from 'lucide-react';
import { Permission } from '@/lib/api/interfaces/permissions.interface';
import { PlanKey } from '@/lib/api/interfaces/billing.interface';
import { PermissionFeatureWrapper } from '@/util-components/auth/PermissionFeatureWrapper';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils/cn';

type CaptionActionMode = 'GENERATE' | 'IMPROVE';

interface CaptionInputProps {
  caption: string;
  setCaption: (caption: string) => void;
  isGeneratingCaption: boolean;
  onGenerateCaption: () => void;
  onImproveCaption: () => void;
  canGenerateAiCaptions: boolean;
  generationCount: number;
  remainingRegenerations: number;
  remainingImprovements: number;
  actionInProgress?: CaptionActionMode | null;
}

export const CaptionInput: React.FC<CaptionInputProps> = ({
  caption,
  setCaption,
  isGeneratingCaption,
  onGenerateCaption,
  onImproveCaption,
  canGenerateAiCaptions,
  generationCount,
  remainingRegenerations,
  remainingImprovements,
  actionInProgress,
}) => {
  const hasCaptionText = caption.trim().length > 0;
  const canRegenerate = remainingRegenerations > 0;
  const canImprove = hasCaptionText && remainingImprovements > 0;
  const shouldPreferImprove = hasCaptionText && canImprove;
  const primaryAction: CaptionActionMode = shouldPreferImprove ? 'IMPROVE' : 'GENERATE';
  const primaryActionDisabled = primaryAction === 'IMPROVE' ? !canImprove : !canRegenerate;
  const hasGeneratedCaptionBefore = generationCount > 0 || remainingRegenerations < 3;

  const handlePrimaryAction = () => {
    if (primaryAction === 'IMPROVE') {
      onImproveCaption();
      return;
    }

    onGenerateCaption();
  };

  const renderPrimaryLabel = () => {
    if (isGeneratingCaption) {
      if (actionInProgress === 'IMPROVE') {
        return (
          <>
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            Verbessert...
          </>
        );
      }

      return (
        <>
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          Generiert...
        </>
      );
    }

    if (primaryAction === 'IMPROVE') {
      return (
        <>
          <Wand2 className="mr-2 h-3.5 w-3.5" />
          Mit KI verbessern ({remainingImprovements})
        </>
      );
    }

    return hasGeneratedCaptionBefore || hasCaptionText ? (
      <>
        <RefreshCcw className="mr-2 h-3.5 w-3.5" />
        Neu generieren ({remainingRegenerations})
      </>
    ) : (
      <>
        <Sparkles className="mr-2 h-3.5 w-3.5" />
        Generieren ({remainingRegenerations})
      </>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <label className="text-sm font-medium text-gray-700">
          Bildunterschrift
          <span className="ml-2 text-xs font-normal text-gray-500">(optional)</span>
        </label>

        <div className="ml-auto">
          <PermissionFeatureWrapper
            requiredPermission={Permission.GENERATE_AI_CAPTIONS}
            requiredPlan={PlanKey.PRO}
          >
            <div className="flex overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handlePrimaryAction}
                disabled={isGeneratingCaption || primaryActionDisabled}
                className="h-9 rounded-none border-0 px-3 text-gray-700 hover:bg-gray-50"
              >
                {renderPrimaryLabel()}
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isGeneratingCaption}
                    className="h-9 rounded-none border-0 border-l border-gray-200 px-2.5 text-gray-600 hover:bg-gray-50"
                  >
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rounded-xl p-1.5">
                  <DropdownMenuItem
                    onClick={onGenerateCaption}
                    disabled={!canRegenerate}
                    className="cursor-pointer items-start rounded-lg px-3 py-2.5"
                  >
                    <RefreshCcw className="mt-0.5 h-4 w-4 text-gray-500" />
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium text-gray-900">
                        {hasGeneratedCaptionBefore || hasCaptionText ? 'Neu generieren' : 'Generieren'}
                      </div>
                      <div className="text-xs leading-5 text-gray-500">
                        Schreibt die Caption komplett neu auf Basis des Post-Kontexts. Noch {remainingRegenerations}x möglich.
                      </div>
                    </div>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={onImproveCaption}
                    disabled={!hasCaptionText || !canImprove}
                    className="cursor-pointer items-start rounded-lg px-3 py-2.5"
                  >
                    <Wand2 className={cn("mt-0.5 h-4 w-4", canImprove ? "text-blue-600" : "text-gray-400")} />
                    <div className="space-y-0.5">
                      <div className="text-sm font-medium text-gray-900">Verbessern</div>
                      <div className="text-xs leading-5 text-gray-500">
                        Überarbeitet deine aktuelle Caption, macht sie klarer und natürlicher. Noch {remainingImprovements}x möglich.
                      </div>
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </PermissionFeatureWrapper>
        </div>
      </div>

      {/* Loading state and Textarea are combined with AnimatePresence */}
      <div className="relative">
        <Textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          disabled={isGeneratingCaption}
          placeholder={
            canGenerateAiCaptions
              ? "Gib deine eigene Caption ein oder nutze die KI, um sie neu zu erstellen oder zu verfeinern."
              : "Verfasse eine Caption – mit Pro kannst du sie per KI generieren oder optimieren."
          }
          className="min-h-[132px] rounded-2xl border-gray-200 px-4 py-3 text-sm focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        />

        <AnimatePresence>
          {isGeneratingCaption && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-10 flex items-center justify-center rounded-2xl overflow-hidden pointer-events-none bg-white/20 backdrop-blur-sm"
            >
              {/* Smooth breathing pulse circles in elegant blues */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.3, 0.6, 0.3],
                }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -top-8 -left-8 w-40 h-40 bg-blue-500/40 rounded-full filter blur-3xl"
              />
              <motion.div
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
                className="absolute top-0 -right-8 w-48 h-48 bg-sky-400/40 rounded-full filter blur-3xl"
              />
              <motion.div
                animate={{
                  scale: [0.9, 1.2, 0.9],
                  opacity: [0.25, 0.55, 0.25],
                }}
                transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 w-48 h-48 bg-indigo-500/30 rounded-full filter blur-3xl"
              />

              {/* Shimmer line overlay - Ultra Smooth */}
              <motion.div
                animate={{ x: ['-200%', '200%'] }}
                transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute inset-0 w-full bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12 scale-150 blur-xl opacity-70"
              />

              {/* Glass overlay highlight */}
              <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {canGenerateAiCaptions && (
        <div className="flex items-center justify-between gap-3 px-1 text-[11px] text-gray-500">
          <span>KI kann deine Caption komplett neu schreiben oder eine bestehende Version verbessern.</span>
          <Link
            to="/vereine/ki-anpassung"
            className="shrink-0 font-medium text-blue-600/80 transition-colors hover:text-blue-700 hover:underline"
          >
            Anpassen
          </Link>
        </div>
      )}
    </div>
  );
};
